// Candle shipping estimate: USPS Ground Advantage (commercial rates, effective
// July 12 2026), shipped from 77840 (College Station, TX). Price = weight tier x
// USPS zone. The site and the Stripe checkout function each carry a copy of this
// file (src/candleShipping.js and functions/candleShipping.js) — keep them
// identical, and update the constants below when USPS changes rates or the
// candle/box changes.

// Packed weight: a filled 10 oz tumbler is ~16 oz, plus ~4 oz of box and padding.
// Rounded up to the next whole pound, since Ground Advantage prices by the pound.
const CANDLE_OZ = 16;
const PACKAGING_OZ = 4;

// Zone used before we know the destination (empty state) and as a safe fallback.
const ESTIMATE_ZONE = 3; // typical Texas destination
const FALLBACK_ZONE = 6;

// Ground Advantage commercial price in cents, by pound tier (2-11 lb) x zones 1-9.
const RATES_CENTS = {
    2: [799, 808, 826, 851, 995, 1158, 1200, 1287, 1287],
    3: [864, 866, 914, 967, 1157, 1359, 1436, 1575, 1575],
    4: [928, 934, 970, 1065, 1284, 1516, 1619, 1801, 1801],
    5: [970, 976, 1014, 1102, 1348, 1589, 1712, 1919, 1919],
    6: [987, 994, 1036, 1155, 1428, 1689, 1831, 2068, 2068],
    7: [996, 1002, 1062, 1190, 1490, 1765, 1923, 2183, 2183],
    8: [1010, 1027, 1149, 1243, 1549, 1834, 2008, 2290, 2290],
    9: [1101, 1125, 1239, 1365, 1611, 1913, 2101, 2409, 2409],
    10: [1191, 1226, 1318, 1444, 1676, 1994, 2197, 2534, 2534],
    11: [1275, 1294, 1400, 1518, 1812, 2128, 2368, 2737, 2737],
};
const MAX_TIER = 11;

// Approximate zone from College Station, TX by destination state (distance bands:
// 1-2 <=150 mi, 3 <=300, 4 <=600, 5 <=1000, 6 <=1400, 7 <=1800, 8 farther).
const STATE_ZONE = {
    LA: 4, OK: 4, AR: 4, MS: 4,
    NM: 5, AL: 5, TN: 5, KS: 5, MO: 5, CO: 5, GA: 5, KY: 5, IN: 5, IL: 5, IA: 5, NE: 5,
    AZ: 6, FL: 6, SC: 6, NC: 6, VA: 6, OH: 6, MI: 6, WV: 6, MD: 6, DC: 6, MN: 6, WI: 6,
    ND: 6, SD: 6, WY: 6, UT: 6,
    NV: 7, ID: 7, MT: 7, CA: 7, PA: 7, NJ: 7, NY: 7, DE: 7, CT: 7, RI: 7, MA: 7,
    OR: 8, WA: 8, NH: 8, VT: 8, ME: 8, AK: 8, HI: 8,
};

// Texas is big, so it is zoned by the first three digits of the ZIP: [from, to, zone].
const TX_ZIP3_ZONES = [
    [733, 733, 2], [750, 754, 3], [755, 757, 3], [758, 759, 2], [760, 762, 3], [763, 763, 4],
    [764, 764, 3], [765, 767, 2], [768, 769, 3], [770, 775, 2], [776, 777, 3], [778, 779, 2],
    [780, 784, 3], [785, 785, 4], [786, 787, 2], [788, 788, 3], [789, 789, 2],
    [790, 794, 4], [795, 796, 3], [797, 797, 4], [798, 799, 5], [885, 885, 5],
];

const STATE_NAMES = {
    ALABAMA: 'AL', ALASKA: 'AK', ARIZONA: 'AZ', ARKANSAS: 'AR', CALIFORNIA: 'CA', COLORADO: 'CO',
    CONNECTICUT: 'CT', DELAWARE: 'DE', 'DISTRICT OF COLUMBIA': 'DC', FLORIDA: 'FL', GEORGIA: 'GA',
    HAWAII: 'HI', IDAHO: 'ID', ILLINOIS: 'IL', INDIANA: 'IN', IOWA: 'IA', KANSAS: 'KS',
    KENTUCKY: 'KY', LOUISIANA: 'LA', MAINE: 'ME', MARYLAND: 'MD', MASSACHUSETTS: 'MA',
    MICHIGAN: 'MI', MINNESOTA: 'MN', MISSISSIPPI: 'MS', MISSOURI: 'MO', MONTANA: 'MT',
    NEBRASKA: 'NE', NEVADA: 'NV', 'NEW HAMPSHIRE': 'NH', 'NEW JERSEY': 'NJ', 'NEW MEXICO': 'NM',
    'NEW YORK': 'NY', 'NORTH CAROLINA': 'NC', 'NORTH DAKOTA': 'ND', OHIO: 'OH', OKLAHOMA: 'OK',
    OREGON: 'OR', PENNSYLVANIA: 'PA', 'RHODE ISLAND': 'RI', 'SOUTH CAROLINA': 'SC',
    'SOUTH DAKOTA': 'SD', TENNESSEE: 'TN', TEXAS: 'TX', UTAH: 'UT', VERMONT: 'VT',
    VIRGINIA: 'VA', WASHINGTON: 'WA', 'WEST VIRGINIA': 'WV', WISCONSIN: 'WI', WYOMING: 'WY',
};

// "Texas", " tx " and "TX" all become "TX"; anything unrecognised becomes "".
function normalizeState(raw) {
    const s = String(raw || '').trim().toUpperCase().replace(/\./g, '');
    if (STATE_NAMES[s]) return STATE_NAMES[s];
    return /^[A-Z]{2}$/.test(s) ? s : '';
}

function shippingZone(state, postalCode) {
    const st = normalizeState(state);
    if (!st) return ESTIMATE_ZONE;
    if (st === 'TX') {
        const zip3 = parseInt(String(postalCode || '').trim().slice(0, 3), 10);
        const hit = TX_ZIP3_ZONES.find(([lo, hi]) => zip3 >= lo && zip3 <= hi);
        return hit ? hit[2] : ESTIMATE_ZONE;
    }
    return STATE_ZONE[st] || FALLBACK_ZONE;
}

// Shipping for `quantity` candles, in cents.
function candleShippingCents({ quantity, state, postalCode }) {
    const qty = Math.max(1, Math.floor(Number(quantity) || 1));
    const pounds = Math.min(MAX_TIER, Math.ceil((qty * CANDLE_OZ + PACKAGING_OZ) / 16));
    return RATES_CENTS[pounds][shippingZone(state, postalCode) - 1];
}

export { candleShippingCents, shippingZone, normalizeState };
